import { useCallback, useEffect, useMemo, useState } from 'react';
import { AppController } from '@/call/app-controller';
import { extractRoomIdFromUrl, generateRoomId, isValidRoomId } from '@/call/room';
import { openCalendar } from '@/call/schedule';
import { logEvent } from '@/config/analytics';
import { createTranslator, detectLocale, type MessageKey } from '@/i18n';
import { CallScreen } from './components/CallScreen';
import { GuestScreen } from './components/GuestScreen';
import { PermissionNotice } from './components/PermissionNotice';
import { StartScreen } from './components/StartScreen';
import { useAppSnapshot } from './useController';

const controller = new AppController();

/** Ідентифікатор із посилання читається один раз при завантаженні сторінки. */
const roomIdFromLink = extractRoomIdFromUrl(window.location.href);

export function App(): React.JSX.Element {
  const locale = useMemo(() => detectLocale(), []);
  const { t } = useMemo(() => createTranslator(locale), [locale]);

  // <html lang> — мова, якою реально показано сторінку, а не завжди `en`
  // з index.html: від неї залежать озвучка скрінрідерів і переклад браузера.
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);
  const snapshot = useAppSnapshot(controller);

  /**
   * Кімната з посилання, для якої ще показується екран гостя. Туторіалів
   * немає (зміна onboarding-share-first): посилання одразу вирішує, хто
   * людина — гість чи хост.
   */
  const [guestRoomId, setGuestRoomId] = useState<string | null>(roomIdFromLink);

  /** Кімнату створено в цій вкладці — тоді в порожній кімнаті картка запрошення. */
  const [createdHere, setCreatedHere] = useState(false);

  /** Вхід у кімнату завжди йде через явну дію користувача. */
  const enterRoom = useCallback((roomId: string, created: boolean) => {
    setCreatedHere(created);
    if (created) logEvent('room_created');
    void controller.joinRoom(roomId);
  }, []);

  const errorText = useMemo(() => {
    if (snapshot.errorKey !== null) return t(snapshot.errorKey as MessageKey);
    switch (snapshot.endReason) {
      case 'peer-left':
        return t('peerLeft');
      case 'room-full':
        return t('roomFull');
      case 'invalid-room':
        return t('roomIdValidationError');
      case 'reconnect-failed':
        return t('reconnectionFailed', { attempts: snapshot.reconnectAttempt });
      case 'time-expired':
        return t('timeExpired');
      case 'connection-failed':
        return t('connectionFailedTryAgain');
      default:
        return null;
    }
  }, [snapshot.errorKey, snapshot.endReason, snapshot.reconnectAttempt, t]);

  if (snapshot.permissionIssue !== null) {
    return (
      <PermissionNotice
        t={t}
        kind={snapshot.permissionIssue}
        onRetry={() => {
          void controller.retryAfterPermissionIssue();
        }}
        onDismiss={() => {
          controller.dismissPermissionIssue();
        }}
      />
    );
  }

  const inCall = snapshot.phase !== 'idle' && snapshot.phase !== 'ended';

  if (!inCall && guestRoomId !== null) {
    const roomId = guestRoomId;
    return (
      <GuestScreen
        t={t}
        valid={isValidRoomId(roomId)}
        onJoin={() => {
          setGuestRoomId(null);
          logEvent('room_joined', { via: 'link' });
          enterRoom(roomId, false);
        }}
        onCreateOwn={() => {
          setGuestRoomId(null);
        }}
      />
    );
  }

  if (!inCall) {
    return (
      <StartScreen
        t={t}
        onCreateRoom={() => {
          enterRoom(generateRoomId(), true);
        }}
        onJoinRoom={(roomId) => {
          enterRoom(roomId, false);
        }}
        onScheduleCall={() => {
          // Кімната створюється наперед, щоб посилання в запрошенні вже було
          // робочим — так само, як у мобільному клієнті.
          const roomId = generateRoomId();
          return { roomId, outcome: openCalendar(roomId) };
        }}
        errorText={errorText}
        onDismissError={() => {
          controller.dismissError();
          controller.reset();
        }}
      />
    );
  }

  return (
    <CallScreen
      t={t}
      snapshot={snapshot}
      createdHere={createdHere}
      errorText={errorText}
      onToggleMic={() => {
        controller.setMicEnabled(!snapshot.micEnabled);
      }}
      onToggleVideo={() => {
        controller.setVideoEnabled(!snapshot.videoEnabled);
      }}
      onToggleScreenShare={() => {
        void (snapshot.screenSharing
          ? controller.stopScreenShare()
          : controller.startScreenShare());
      }}
      onToggleMirror={() => {
        controller.toggleMirror();
      }}
      onToggleFit={() => {
        controller.toggleRemoteFit();
      }}
      onToggleBlock={() => {
        controller.toggleRemoteControlBlocked();
      }}
      onHangUp={() => {
        controller.endCall();
      }}
      onDismissError={() => {
        controller.dismissError();
      }}
    />
  );
}
